import { useState } from "react";
import { AI_PROVIDER_INFO, NPC_ASK_DAILY_CAP } from "@greg-chick/shared";

/**
 * 💰 **AI 자유 대화 — AI별 횟수와 비용** (조치계획서 ■10).
 *
 * ## 왜 (2026-09-25)
 * ■10 은 「AI 자유 대화 실사용(10월 한 달)」을 **AI별 횟수**와 함께 증빙으로 내겠다고 적었다.
 * 엣지는 9. 21. 부터 AI별로 세고 있었는데, 이 화면은 **합계 한 줄**만 보여 줬다 —
 * 운영자가 그 칸을 채울 길이 없었다(세어 놓고 보여 주지 않은 것).
 *
 * ## 🔴 단가를 박지 않는다 — 청구서가 정답이다
 * AI 요금은 호출이 아니라 **글자(토큰) 수**로 붙는다. 「1회당 몇 원」을 코드에 적으면
 * 그건 추측이고, 모델·질문 길이가 바뀌면 조용히 틀린다. 그래서 운영자가 **각 AI 콘솔의
 * 청구액**을 넣으면, 그 기간 횟수로 나눠 1회당을 **계산**한다.
 *
 * 🔴 **저장하지 않는다.** 계산기다 — 적어 둔 청구액이 다음 달에 남아 있으면 틀린 숫자를
 * 보게 된다. 넣고, 읽고, 보고서에 옮긴다.
 */

/** 이 행이 무엇인지 사람 말로 — 운영자는 「anthropic」이 아니라 「Claude」로 안다. */
export function docentKeySay(key: string): string {
  if (key === "fallback") return "버튼 답으로 되돌아감";
  return (AI_PROVIDER_INFO as Record<string, { label: string } | undefined>)[key]?.label ?? key;
}

export interface DocentCostRow {
  key: string;
  n: number;
  /** 운영자가 넣은 이 기간 청구액(원). 비었으면 undefined. */
  bill?: number;
}

/**
 * 1회당 비용과 **한 달 최대**를 계산한다(순수 — 검사가 직접 부른다).
 *
 * 한 달 최대 = 하루 상한 × 30일 × **넣은 것 중 가장 비싼 1회당**. NPC 마다 AI 가 따로 정해져
 * 있어 섞여 쓰이므로, 가장 비싼 쪽으로 다 쓴다고 보면 **넘지 않는 선**이 된다.
 */
export function docentCost(rows: readonly DocentCostRow[]) {
  const paid = rows.filter((r) => r.key !== "fallback");
  const perCall = paid.map((r) => ({
    key: r.key,
    each: r.bill !== undefined && r.n > 0 ? r.bill / r.n : undefined,
  }));
  const known = perCall.map((p) => p.each).filter((v): v is number => v !== undefined);
  const worstEach = known.length ? Math.max(...known) : undefined;
  return {
    perCall,
    calls: paid.reduce((s, r) => s + r.n, 0),
    monthCap: NPC_ASK_DAILY_CAP * 30,
    monthMax: worstEach === undefined ? undefined : Math.round(worstEach * NPC_ASK_DAILY_CAP * 30),
  };
}

const won = (v: number) => `${Math.round(v).toLocaleString()}원`;

export function DocentCost({ list, periodSay }: { list: readonly (readonly [string, number])[]; periodSay: string }) {
  const [bills, setBills] = useState<Record<string, string>>({});
  if (list.length === 0) return null;

  const rows: DocentCostRow[] = list.map(([key, n]) => {
    const raw = (bills[key] ?? "").replace(/[^\d]/g, "");
    return { key, n, ...(raw ? { bill: Number(raw) } : {}) };
  });
  const c = docentCost(rows);
  const eachOf = (key: string) => c.perCall.find((p) => p.key === key)?.each;

  return (
    <div className="stats__group docentcost">
      <h3>🤖 AI 자유 대화 — AI별 횟수와 비용</h3>
      <p className="stats__note">
        <b>{periodSay}</b> 동안 모델까지 간 대화 <b>{c.calls.toLocaleString()}회</b>. 청구액은 각 AI 콘솔(Anthropic ·
        OpenAI · Google)에서 같은 기간 금액을 보고 넣으세요 — 여기에는 <b>저장되지 않아요</b>(계산만 해요).
      </p>
      <table className="docentcost__table">
        <thead>
          <tr>
            <th>AI</th>
            <th>횟수</th>
            <th>이 기간 청구액</th>
            <th>1회당</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td>{docentKeySay(r.key)}</td>
              <td className="docentcost__n">{r.n.toLocaleString()}</td>
              {r.key === "fallback" ? (
                <td colSpan={2} className="docentcost__muted">
                  모델이 답을 못 줘서 버튼으로 돌아간 것 — 비용 계산에서 뺍니다
                </td>
              ) : (
                <>
                  <td>
                    <input
                      id={`docentcost-bill-${r.key}`}
                      inputMode="numeric"
                      placeholder="예: 3500"
                      aria-label={`${docentKeySay(r.key)} 이 기간 청구액(원)`}
                      value={bills[r.key] ?? ""}
                      onChange={(e) => setBills((b) => ({ ...b, [r.key]: e.target.value }))}
                    />
                  </td>
                  <td className="docentcost__n">{eachOf(r.key) === undefined ? "—" : won(eachOf(r.key)!)}</td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {c.monthMax !== undefined ? (
        <p className="docentcost__cap">
          하루 상한 <b>{NPC_ASK_DAILY_CAP}회</b> × 30일 = {c.monthCap.toLocaleString()}회를 가장 비싼 1회당으로 다 쓰면
          한 달 최대 <b>{won(c.monthMax)}</b> — 조치계획서 ■10 은 3만 원으로 잡았어요.
        </p>
      ) : null}
      <p className="docentcost__warn">
        🔴 같은 AI 열쇠로 <b>굿즈 초안·문답 초안·그림 글자 읽기</b>도 불러요. 콘솔 청구액에는 그것도 섞여 있어서
        1회당은 <b>실제보다 크게</b> 나와요 — 상한을 따지기에는 넉넉한 쪽이에요.
      </p>
    </div>
  );
}
